import { nextTick, onUnmounted, Ref, watch } from 'vue'

import {
  BOUNDARY_ELEMENT_ID_SUFFIX,
  COLUMN_CONSTRAIN_PADDING_PX,
  COLUMN_FILLER_MARGIN_PX,
  COLUMN_LINE_FILLER_CLASS,
  COLUMN_LINE_GROUPING_TOLERANCE_PX,
  COLUMN_MEASURING_CLASS,
  COLUMN_SHORT_LINE_THRESHOLD,
  COLUMN_WIDTH_THRESHOLD,
  STRETCH_POINT_CLASS,
  WORD_ELEMENT_ID_SUFFIX,
} from '@/constants/practiceConstants'

/** Walk backwards from an element to find the line-top of the preceding word element. */
function findLineTopForElement(el: Element, wordToLine: Map<HTMLElement, number>): number | undefined {
  let sibling: Element | null = el.previousElementSibling
  while (sibling && !sibling.id?.endsWith(WORD_ELEMENT_ID_SUFFIX)) {
    sibling = sibling.previousElementSibling
  }
  if (!sibling) {
    return undefined
  }
  return wordToLine.get(sibling as HTMLElement)
}

/**
 * Column justification for Torah column mode.
 *
 * CSS does the heavy lifting: `.column-justify` on the container sets
 * `text-align: justify` + `text-align-last: justify` (needed because every
 * line ends with a forced `<br>` and CSS justify skips lines before forced
 * breaks unless text-align-last is set).
 *
 * This composable handles three things:
 * 1. Constrain the container width to the widest natural line so the browser
 *    doesn't spread words across the full parent width (~1120px).
 * 2. Expand `.stretch-point` elements on lines with setuma/petucha section gaps
 *    so they absorb slack and create a visible gap between sections.
 * 3. Set filler widths on short lines WITHOUT stretch points (petucha breaks)
 *    so justify doesn't over-spread their few words.
 */
export function useColumnJustification(containerRef: Ref<HTMLElement | null>, columnMode: Ref<boolean>, deps: Ref<unknown>[]) {
  let resizeObserver: ResizeObserver | null = null
  let selfResizing = false

  function reset(container: HTMLElement) {
    container.style.maxWidth = ''
    container.style.marginInline = ''
  }

  function resetDynamicWidths(container: HTMLElement) {
    for (const el of container.querySelectorAll<HTMLElement>(`.${COLUMN_LINE_FILLER_CLASS}`)) {
      el.style.width = '0'
    }
    for (const el of container.querySelectorAll<HTMLElement>(`.${STRETCH_POINT_CLASS}`)) {
      el.style.width = '0'
    }
  }

  function apply() {
    const container = containerRef.value
    if (!container || !columnMode.value) {
      console.log('useColumnJustification apply(): early return (container=', !!container, 'columnMode=', columnMode.value, ')')
      return
    }
    console.log('useColumnJustification apply(): RUNNING, container classes=', container.className)

    // 1. Disable justify and reset constraints for accurate measurement
    container.classList.add(COLUMN_MEASURING_CLASS)
    reset(container)
    resetDynamicWidths(container)
    void container.offsetHeight

    const containerWidth = container.getBoundingClientRect().width
    if (containerWidth <= 0) {
      container.classList.remove(COLUMN_MEASURING_CLASS)
      return
    }

    // 2. Measure natural line widths by grouping inline elements by visual line.
    //    Include both word elements and boundary elements (segment boundaries like
    //    maqaf can appear outside the word bounding box after a line break).
    const wordEls = container.querySelectorAll<HTMLElement>(`[id$="${WORD_ELEMENT_ID_SUFFIX}"]`)
    if (!wordEls.length) {
      container.classList.remove(COLUMN_MEASURING_CLASS)
      return
    }

    const lineRects = new Map<number, DOMRect[]>()
    const wordToLine = new Map<HTMLElement, number>()

    const findLineKey = (top: number): number => {
      for (const k of lineRects.keys()) {
        if (Math.abs(top - k) <= COLUMN_LINE_GROUPING_TOLERANCE_PX) {
          return k
        }
      }
      return top
    }

    for (const el of wordEls) {
      const rect = el.getBoundingClientRect()
      const key = findLineKey(Math.round(rect.top))
      if (!lineRects.has(key)) {
        lineRects.set(key, [])
      }
      lineRects.get(key)!.push(rect)
      wordToLine.set(el, key)
    }

    // Also include boundary elements (segment boundaries like maqaf ־) in line
    // width measurement — they take up space but aren't word elements.
    const boundaryEls = container.querySelectorAll<HTMLElement>(`[id$="${BOUNDARY_ELEMENT_ID_SUFFIX}"]`)
    for (const el of boundaryEls) {
      const rect = el.getBoundingClientRect()
      if (rect.width <= 0) {
        continue
      }
      const key = findLineKey(Math.round(rect.top))
      if (lineRects.has(key)) {
        lineRects.get(key)!.push(rect)
      }
    }

    let maxNatural = 0
    const lineWidths = new Map<number, number>()
    for (const [lineTop, rects] of lineRects) {
      const right = Math.max(...rects.map((r) => r.right))
      const left = Math.min(...rects.map((r) => r.left))
      const width = right - left
      lineWidths.set(lineTop, width)
      maxNatural = Math.max(maxNatural, width)
    }

    // DEBUG: Log all lines with their widths and word counts
    const sortedLines = [...lineWidths.entries()].sort((a, b) => a[0] - b[0])
    console.log(
      `useColumnJustification: ${sortedLines.length} lines detected, containerWidth=${containerWidth.toFixed(0)}px, maxNatural=${maxNatural.toFixed(0)}px`,
    )
    sortedLines.forEach(([top, width], i) => {
      const wordCount = lineRects.get(top)?.length ?? 0
      console.log(`  line ${i + 1}/@${top}: width=${width.toFixed(0)}px, words=${wordCount}`)
    })

    // 3. Constrain container if content is much narrower than container
    let columnWidth = containerWidth
    if (maxNatural > 0 && maxNatural < containerWidth * COLUMN_WIDTH_THRESHOLD) {
      columnWidth = Math.ceil(maxNatural) + COLUMN_CONSTRAIN_PADDING_PX
      console.log(`useColumnJustification: constraining ${containerWidth.toFixed(0)}px → ${columnWidth}px`)
      selfResizing = true
      container.style.maxWidth = `${columnWidth}px`
      container.style.marginInline = 'auto'
      requestAnimationFrame(() => {
        selfResizing = false
      })
    }

    // 4a. Expand stretch points for lines with setuma/petucha section gaps.
    //     The stretch absorbs the line's slack, creating a visible gap while
    //     keeping words at natural spacing on both sides.
    const linesWithStretch = new Set<number>()
    const stretchEls = container.querySelectorAll<HTMLElement>(`.${STRETCH_POINT_CLASS}`)

    for (const stretchEl of stretchEls) {
      const lineTop = findLineTopForElement(stretchEl, wordToLine)
      if (lineTop === undefined) {
        continue
      }

      linesWithStretch.add(lineTop)

      const lineWidth = lineWidths.get(lineTop)
      if (lineWidth === undefined) {
        continue
      }

      const slack = columnWidth - lineWidth
      if (slack > 0) {
        stretchEl.style.width = `${slack}px`
        console.log(`useColumnJustification: stretch on line@${lineTop} — gap=${slack.toFixed(0)}px`)
      }
    }

    // 4b. Set fillers on short lines WITHOUT stretch points so justify doesn't
    //     over-spread their words.
    const fillerEls = container.querySelectorAll<HTMLElement>(`.${COLUMN_LINE_FILLER_CLASS}`)

    for (const fillerEl of fillerEls) {
      const lineTop = findLineTopForElement(fillerEl, wordToLine)
      if (lineTop === undefined) {
        continue
      }

      // Skip lines where a stretch point already absorbs the slack
      if (linesWithStretch.has(lineTop)) {
        continue
      }

      const lineWidth = lineWidths.get(lineTop)
      if (lineWidth === undefined) {
        continue
      }

      const slack = columnWidth - lineWidth
      const slackPct = slack / columnWidth
      if (slackPct > COLUMN_SHORT_LINE_THRESHOLD) {
        const fillerWidth = Math.max(0, slack - COLUMN_FILLER_MARGIN_PX)
        console.log(
          `useColumnJustification: short line@${lineTop} — ${lineWidth.toFixed(0)}px, slack=${slack.toFixed(0)}px (${(slackPct * 100).toFixed(0)}%), filler=${fillerWidth.toFixed(0)}px`,
        )
        fillerEl.style.width = `${fillerWidth}px`
      }
    }

    // DEBUG: Log filler and break elements
    const allFillers = container.querySelectorAll<HTMLElement>(`.${COLUMN_LINE_FILLER_CLASS}`)
    const allBrs = container.querySelectorAll('br')
    console.log(`useColumnJustification: ${allFillers.length} fillers, ${allBrs.length} <br> elements`)

    // DEBUG: Check the last few child nodes of the container
    const lastChildren = [...container.childNodes].slice(-6)
    console.log(
      'useColumnJustification: last container children:',
      lastChildren.map((n) => {
        if (n.nodeType === 1) {
          const el = n as HTMLElement
          return `<${el.tagName.toLowerCase()} class="${el.className}">`
        }
        if (n.nodeType === 3) {
          return `text:"${(n as Text).textContent?.slice(0, 20)}"`
        }
        return `node(${n.nodeType})`
      }),
    )

    // DEBUG: Check computed styles on last line vs previous line
    if (sortedLines.length >= 2) {
      const lastLineTop = sortedLines[sortedLines.length - 1][0]
      const prevLineTop = sortedLines[sortedLines.length - 2][0]
      const lastLineRects = lineRects.get(lastLineTop) ?? []
      const prevLineRects = lineRects.get(prevLineTop) ?? []
      if (lastLineRects.length > 0 && prevLineRects.length > 0) {
        const lastLineBottom = Math.max(...lastLineRects.map((r) => r.bottom))
        const prevLineBottom = Math.max(...prevLineRects.map((r) => r.bottom))
        const lastLineHeight = lastLineBottom - lastLineTop
        const prevLineHeight = prevLineBottom - prevLineTop
        const gapBetween = lastLineTop - prevLineBottom
        console.log(
          `useColumnJustification: prev line height=${prevLineHeight.toFixed(1)}px, last line height=${lastLineHeight.toFixed(1)}px, gap between=${gapBetween.toFixed(1)}px`,
        )

        // Check computed line-height on first word of each line
        for (const [lineTop] of [
          [prevLineTop, lineRects.get(prevLineTop)],
          [lastLineTop, lineRects.get(lastLineTop)],
        ] as [number, DOMRect[]][]) {
          // Find the actual word element at this lineTop
          for (const el of wordEls) {
            const rect = el.getBoundingClientRect()
            if (Math.abs(Math.round(rect.top) - lineTop) <= 4) {
              const cs = getComputedStyle(el)
              const parentCs = el.parentElement ? getComputedStyle(el.parentElement) : null
              const grandParentCs = el.parentElement?.parentElement ? getComputedStyle(el.parentElement.parentElement) : null
              console.log(
                `useColumnJustification: word on line@${lineTop}: lineHeight=${cs.lineHeight}, fontSize=${cs.fontSize}, parent.lineHeight=${parentCs?.lineHeight}, grandparent.lineHeight=${grandParentCs?.lineHeight}`,
              )
              break
            }
          }
        }

        // Also check container's computed line-height
        console.log(
          `useColumnJustification: container computed lineHeight=${getComputedStyle(container).lineHeight}, fontSize=${getComputedStyle(container).fontSize}`,
        )
      }
    }

    // 5. Re-enable CSS justify (the .column-justify class handles the rest)
    container.classList.remove(COLUMN_MEASURING_CLASS)
  }

  function schedule() {
    nextTick(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(apply)
      })
    })
  }

  const stopWatchers = deps.map((dep, i) =>
    watch(dep, () => {
      console.log(`useColumnJustification: dep[${i}] changed → schedule()`)
      schedule()
    }),
  )
  const stopColumnMode = watch(columnMode, (val) => {
    console.log('useColumnJustification: columnMode changed →', val)
    if (val) {
      schedule()
    } else if (containerRef.value) {
      reset(containerRef.value)
      resetDynamicWidths(containerRef.value)
    }
  })

  const stopContainer = watch(
    containerRef,
    (el) => {
      console.log('useColumnJustification: containerRef changed →', !!el, 'columnMode=', columnMode.value)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
      if (el && columnMode.value) {
        resizeObserver = new ResizeObserver(() => {
          if (!selfResizing) {
            console.log('useColumnJustification: ResizeObserver → apply()')
            apply()
          }
        })
        resizeObserver.observe(el)
        schedule()
      }
    },
    { immediate: true },
  )

  onUnmounted(() => {
    resizeObserver?.disconnect()
    stopWatchers.forEach((stop) => stop())
    stopColumnMode()
    stopContainer()
  })

  return { justify: schedule }
}
